"use client";

import ProgramNavigation from "@12-week/components/ProgramNavigation";
import VideoPlayerCard from "@12-week/components/VideoPlayerCard";
import type { ProgramNavigationProps, Video } from "@12-week/lib/types";
import Breadcrumb from "@packages/components/Breadcrumb";
import PageHeading from "@packages/components/PageHeading";
import Card from "@packages/components/ui/Card";
import CardStack from "@packages/components/ui/CardStack";
import Heading from "@packages/components/ui/Heading";
import HorizontalRule from "@packages/components/ui/HorizontalRule";
import { notFound } from "next/navigation";
import React from "react";

export default function VideoPage({
  video,
  breadcrumbBasePath,
  breadcrumbBaseName,
  children,
  currentSlug,
  program,
  fromProgram,
  fromWeek,
  nutritionEntries,
  nutritionIntroHref,
  nutritionEntryHref,
  nutritionIntroLabel,
  overline,
  videoHeading,
  videoSubheading,
  videoIntroText,
  currentTime,
  streamRef,
  belowIntroVideo,
  hideProgramNavigation,
}: {
  video: Video;
  breadcrumbBasePath?: string;
  breadcrumbBaseName?: string;
  children?: React.ReactNode;
  currentSlug: string;
  program: ProgramNavigationProps["program"];
  fromProgram?: string;
  fromWeek?: string;
  nutritionEntries?: ProgramNavigationProps["nutritionEntries"];
  nutritionIntroHref?: ProgramNavigationProps["nutritionIntroHref"];
  nutritionEntryHref?: ProgramNavigationProps["nutritionEntryHref"];
  nutritionIntroLabel?: ProgramNavigationProps["nutritionIntroLabel"];
  overline?: string;
  videoHeading?: string;
  videoSubheading?: string;
  videoIntroText?: React.ReactNode;
  showSupportSystem?: boolean;
  currentTime?: number;
  videoRef?: React.RefObject<HTMLVideoElement>;
  streamRef?: React.MutableRefObject<
    import("@cloudflare/stream-react").StreamPlayerApi | undefined
  >;
  belowIntroVideo?: React.ReactNode;
  hideProgramNavigation?: boolean;
}) {
  if (!video) {
    notFound();
  }

  let pages;
  if (currentSlug === "/" || currentSlug === "") {
    // On the intro/root page, only show the base as current
    pages = [{ name: breadcrumbBaseName || "Home", href: null, current: true }];
  } else {
    // On sub pages, show base as parent and video as current
    pages = [
      {
        name: breadcrumbBaseName || "Home",
        href: breadcrumbBasePath ?? null,
        current: false,
      },
      {
        name: video.title?.split(":")[0]?.trim() || "Current Video",
        href: null,
        current: true,
      },
    ];
  }

  return (
    <>
      {breadcrumbBasePath && breadcrumbBaseName && <Breadcrumb pages={pages} />}
      <article aria-labelledby="video-title" className="space-y-12">
        <PageHeading
          title={video.pageTitle || video.title || ""}
          subtitle={video.subtitle || video.description || ""}
          overline={overline || video.overline || ""}
        />
        {/* VideoPlayer section */}
        <CardStack>
          <section className="relative">
            {videoHeading || videoSubheading || videoIntroText ? (
              <Card>
                <CardStack variant="sub">
                  {videoHeading && (
                    <Heading
                      level={2}
                      id={videoHeading.replace(/\s+/g, "-").toLowerCase()}
                      heading={videoHeading}
                      subheading={videoSubheading}
                      description={videoIntroText}
                    />
                  )}
                  {videoIntroText && <HorizontalRule variant="sub" />}
                  <div className="my-6">
                    <VideoPlayerCard
                      video={video}
                      currentTime={currentTime}
                      variant="sub"
                      as="div"
                      streamRef={streamRef}
                    />
                  </div>
                  {belowIntroVideo && belowIntroVideo}
                </CardStack>
              </Card>
            ) : (
              <VideoPlayerCard
                video={video}
                currentTime={currentTime}
                as="section"
                streamRef={streamRef}
              />
            )}
          </section>
          {children}
        </CardStack>
      </article>
      {!hideProgramNavigation && (
        <ProgramNavigation
          currentSlug={currentSlug}
          program={program}
          fromProgram={fromProgram}
          fromWeek={fromWeek}
          nutritionEntries={nutritionEntries}
          nutritionIntroHref={nutritionIntroHref}
          nutritionEntryHref={nutritionEntryHref}
          nutritionIntroLabel={nutritionIntroLabel}
        />
      )}
    </>
  );
}
